import { useMemo, useState } from 'react';
import { useFela } from 'react-fela';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import { useAppSelector } from '@/hooks';
import { SecretType } from '@/modules/secrets/constants';
import { setSecretTypeFilter } from '@/modules/secrets/services/actions';
import type { SecretsState } from '@/modules/secrets/services/reducers';
import { selectSecrets } from '@/modules/secrets/services/selectors';

import { SecretTypeIcon } from '../../../SecretTypeIcon';
import { SelectFilter } from '../SelectFilter';
import * as felaRules from './SecretTypeFilter.styles';

type SecretTypeFilterValue = SecretsState['secretType'] | 'all';

export const SecretTypeFilter = () => {
    const { secretType } = useAppSelector(selectSecrets);
    const [value, setValue] = useState<SecretTypeFilterValue>(secretType ?? 'all');
    const { css } = useFela();

    const options = useMemo(() => {
        return [
            'all',
            SecretType.Password,
            'passkey',
            SecretType.PaymentCard,
            SecretType.SecureNote,
            SecretType.ApiCredentials,
            SecretType.DatabaseCredentials,
            SecretType.SshKey,
        ] satisfies SecretTypeFilterValue[];
    }, []);
    const dispatch = useDispatch();

    return (
        <SelectFilter
            label={{ id: 'secret.filter.category' }}
            value={value}
            onChange={(type: SecretTypeFilterValue) => {
                dispatch(setSecretTypeFilter(type === 'all' ? null : type));
                setValue(type);
            }}
            options={options.map(type => ({
                label: <FormattedMessage id={`secret.type.${type}`} />,
                value: type,
            }))}
            optionRender={option => (
                <div className={css(felaRules.option)}>
                    {option.value === 'all' || !option.value ? (
                        <div className={css(felaRules.empty)} />
                    ) : (
                        <SecretTypeIcon type={option.value as SecretType} />
                    )}
                    <span>{option.label}</span>
                </div>
            )}
        />
    );
};
